import $ from 'jquery';
import { Subscription } from 'rxjs';
import { Avatar } from 'phx-core';

import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  Input,
  HostListener,
  ChangeDetectionStrategy,
  ViewChild,
  OnDestroy,
  OnInit,
} from '@angular/core';

import {
  Router,
  NavigationStart,
  RouterEvent,
} from '@angular/router';

import { Session } from '@services/session';
import { FleetCardsFeatureFlagService } from '@services/feature-flags/fleet-cards';
import { SidebarService, ISidebarState } from '@services/sidebar.service';
import { FleetGrowthFeatureFlagService } from '@services/feature-flags/growth';
import { PendoAnalytics } from '@services/pendo-analytics';

import { mapValueToBoolean, nameInitials } from '@lib/utils';
import { getCookie } from '@lib/cookies';
import { SUPPORT_FLEET } from '@lib/customer-support.const';
import { CookieKey } from '@lib/cookie-keys.const';

import { GeneralUpgradeModalComponent } from '@misc/upsell/general-upgrade-modal.component';
import { CompanySwitcherService } from './company-switcher/company-switcher.service';

import { MY_CARD_OPEN_FROM, MY_CARD_TRACK_EVENTS } from '@app/admin/fleet-card/main.constants';

@Component({
  selector: 'avatar-dropdown',
  templateUrl: './avatar-dropdown.component.html',
  styleUrls: ['./avatar-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvatarDropdownComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class.collapse-sidebar') public isSideBarCollapsed: boolean;
  @HostBinding('class.light-theme') @Input() public isLightTheme: boolean;
  @HostBinding('class.placement-top-right') @Input() public placementTopRight: boolean;
  @Input() public reloadAppForSettings: boolean;
  public isAppVersionSwitchingEnabled: boolean = true;
  public userNameInitials: string;
  public userName: string;
  public userEmail: string;
  public isDropdownOpen = false;
  public isUpsellEnabled: boolean;
  public readonly avatarIcon: string = Avatar;
  public supportFleet: string = SUPPORT_FLEET;
  public isCompanySwitcherEnabled: boolean;
  public isFleetCardsFleetUserCardsEnabled: boolean = false;
  public clientAppVerion: string;
  @ViewChild('generalUpgradeModal', { static: false }) public generalUpgradeModal: GeneralUpgradeModalComponent;
  private $el: any;
  private routerSubscription: Subscription;
  private $dropdown;
  public myCardModuleLoaded: boolean = false;
  public userHasActiveFleetUserCard: boolean = false;

  constructor(
    private el: ElementRef,
    private session: Session,
    private router: Router,
    private pendo: PendoAnalytics,
    private sidebarService: SidebarService,
    private companySwitcherService: CompanySwitcherService,
    private fgFeatureFlagService: FleetGrowthFeatureFlagService,
    private fleetCardsFeatureFlagService: FleetCardsFeatureFlagService,
  ) {
    this.$el = $(this.el.nativeElement);
    this.userNameInitials = nameInitials(this.session.getUser());
    this.userName = this.session.getUserFullName();
    this.userEmail = this.session.getUserEmail();
    this.isUpsellEnabled = fgFeatureFlagService.isDashboardUpsellsEnabled();
    this.isCompanySwitcherEnabled = this.session.isCompanySwitcherEnabled();
    this.userHasActiveFleetUserCard = this.session.hasActiveFleetUserCard();
    this.isFleetCardsFleetUserCardsEnabled = this.fleetCardsFeatureFlagService.isFleetCardsFleetUserCardsEnabled();
    this.clientAppVerion = CLIENT_APP_VERSION.split('-')[0];
  }

  public ngOnInit() {
    this.sidebarService.stateListener.subscribe((state: ISidebarState) => {
      this.isSideBarCollapsed = state === 'collapsed';
    });

    this.sidebarService
      .isMyCardsModuleLoaded()
      .subscribe((isLoaded: boolean) => {
        if (isLoaded) {
          this.myCardModuleLoaded = isLoaded;
        }
      });
  }

  public get upsellLabel(): string {
    const showUpsellUpgradeBtn: boolean = mapValueToBoolean(getCookie(CookieKey.UPSELL_UPGRADE_BTN_SHOW));
    if (
      showUpsellUpgradeBtn || this.session.isFeatureTrial()
      || this.session.isTrialEnded() || this.session.isUpgradeRequested
    ) {
      return $localize`Upgrade to Pro`;
    } else {
      return this.session.isTrialEnded() || (this.session.isDispatcher()
        && !this.session.isAdmin()) ? $localize`Upgrade to Pro` : $localize`Try for Free`;
    }
  }

  public get showUpgradeToPro(): boolean {
    return this.isUpsellEnabled && (this.session.isCompanyStarterPlan()
      || this.session.isCompanyEldStarter());
  }

  public get canSwitchCompany(): boolean {
    return this.companySwitcherService.canSwitchCompany;
  }

  public ngAfterViewInit(): void {
    this.$dropdown = this.$el.find('.dropdown');
    this.routerSubscription = this.router.events
      .subscribe((e: RouterEvent) => { if (e instanceof NavigationStart) this.closeIfOpen(); })
    ;
  }

  public ngOnDestroy(): void {
    this.routerSubscription.unsubscribe();
  }

  public toggleAccountSettingMenu(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
    this.$dropdown.transition('fade');
  }

  @HostListener('window:click', ['$event'])
  public onWindowClick(event: MouseEvent): void {
    if (!this.$el.has(event.target).length) this.closeIfOpen();
  }

  public closeIfOpen(): void {
    if (this.isDropdownOpen) this.toggleAccountSettingMenu();
  }

  public reloadSettings(): void {
    if (this.reloadAppForSettings) {
      window.location.href = `${window.location.origin}${window.location.pathname}#/settings/account`;
      window.location.reload();
    }
  }

  public showMyCardDrawer(): void {
    this.sidebarService.showMyCardDrawer();
    this.pendo.trackEvent(MY_CARD_TRACK_EVENTS.OPEN_MY_CARD_PANEL, {
      openedFrom: MY_CARD_OPEN_FROM.SIDEBAR,
      companyId: this.session.getCompany().id,
      userEmail: this.session.getUserEmail(),
    });
  }
}
